import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  deviceSelected,
  deviceSelectedFulfilled,
  deviceSelectedRejected,
  refreshUser,
  removedUserDevice,
  updateUserDevice,
  removeUserDevice,
  selectUser,
  updateUser,
  updateUserRejected
} from '@core/modules/user/features/user.slice';
import { UserService } from '@core/modules/user/services/user.service';
import { NotifierService } from '@core/services/notifier.service';
import { DialogService } from '@shared/dialog/services/dialog.service';

@Injectable()
export class DevicesEffects {
  devicesSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deviceSelected),
      switchMap((action) =>
        this.userService.getDevicesBySerials(action.payload.map((d) => d.serial)).pipe(
          map((devices) => deviceSelectedFulfilled(devices)),
          catchError(() => of(deviceSelectedRejected()))
        )
      )
    )
  );

  addUserDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserDevice),
      switchMap((action) => [
        updateUser({
          userData: action.payload.userData,
          informer: action.payload.hasNewDevices
            ? {
                message: 'dialog.update-devices.message',
                title: 'dialog.update-devices.title',
                buttonDeleteLabel: 'buttons.labels.ok',
                buttonCancelLabel: undefined,
                messageTranslateValues: {},
                config: {
                  panelClass: 'indiba-confirmation-dialog',
                  buttonCancelClass: 'hidden',
                  buttonDeleteClass:
                    'indiba-confirmation-dialog__button indiba-confirmation-dialog__apply-btn'
                }
              }
            : undefined
        })
      ])
    )
  );

  removeUserDevice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(removeUserDevice),
        switchMap((action) =>
          this.dialogService
            .openConfirmDialog(
              'dialog.remove-device.confirm.message',
              'dialog.remove-device.confirm.title',
              'buttons.labels.remove',
              undefined,
              { serialNumber: action.payload.device.serial },
              {
                panelClass: 'indiba-confirmation-dialog',
                buttonCancelClass:
                  'indiba-confirmation-dialog__button indiba-confirmation-dialog__cancel-btn',
                buttonDeleteClass:
                  'indiba-confirmation-dialog__button indiba-confirmation-dialog__apply-btn'
              }
            )
            .pipe(
              filter((confirmed) => confirmed),
              map(() => action)
            )
        ),
        switchMap((action) =>
          this.userService.deleteDeviceBySerial(action.payload).pipe(
            map(() => removedUserDevice({ device: action.payload.device })),
            tap(() => {
              this.notifierService.openSuccessToast('profile.update.success');
            }),
            catchError(() => of(updateUserRejected()))
          )
        )
      )
    //{ dispatch: false }
  );

  refreshAfterRemovedUserDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removedUserDevice),
      concatLatestFrom(() => [this.store.select(selectUser)]),
      switchMap(([, user]) => [refreshUser(user!.email)])
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly userService: UserService,
    private readonly notifierService: NotifierService,
    private readonly dialogService: DialogService
  ) {}
}
